import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import RightArrow from '@/images/arrows/RightArrow.svg';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import { AvatarCreateTab, AvatarType } from '@/pages/avatar/common/types';
import { useAnalytics } from '@/services/analytics';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';

const StyledButton = styled(Button)`
    width: 16rem;
    font-size: 1.6rem;
    margin: 1px 15px 0 0;
    padding: 5px 16px 4px;
    display: flex;
    gap: 6px;
    align-items: center;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-wrap: nowrap;
`;

interface Props {
    setCreateConfirmationModal: (value: boolean) => void;
}

export const HeaderButtons = observer(({ setCreateConfirmationModal }: Props) => {
    const mixpanel = useAnalytics();

    const createAvatarStore = useCreateAvatarStore();

    const firstTab = 0;
    const lastTab = createAvatarStore.tabs.length - 1;
    const beforeLastTab = lastTab - 1;

    const handleBack = () => {
        if (createAvatarStore.tab === firstTab) {
            return;
        }

        mixpanel?.track('avatar-create', {
            type: AvatarType.PremiumPlus,
            event: 'click',
            action: 'back',
            page: AvatarCreateTab[createAvatarStore.tab],
        });
        createAvatarStore.setTab(createAvatarStore.tab - 1);
    };

    const handleNext = (action: 'next' | 'submit') => () => {
        if (createAvatarStore.tab === AvatarCreateTab.WAIT_FOR_UPLOADS) {
            return;
        }

        mixpanel?.track('avatar-create', {
            type: AvatarType.PremiumPlus,
            event: 'click',
            action,
            page: AvatarCreateTab[createAvatarStore.tab],
        });

        if (action === 'submit') {
            setCreateConfirmationModal(true);
        } else {
            createAvatarStore.setTab(createAvatarStore.tab + 1);
        }
    };

    const nextDisabled = createAvatarStore.tab === AvatarCreateTab.FOOTAGE && !createAvatarStore.isFootageConfirmed;
    const createDisabled = createAvatarStore.tab === AvatarCreateTab.CONSENT && !createAvatarStore.isValid;

    return (
        <ButtonsContainer>
            {![firstTab, AvatarCreateTab.WAIT_FOR_UPLOADS].includes(createAvatarStore.tab) && (
                <StyledButton variant="outlined" color="secondary" onClick={handleBack} aria-label="Back">
                    Back
                </StyledButton>
            )}

            {![beforeLastTab, AvatarCreateTab.WAIT_FOR_UPLOADS].includes(createAvatarStore.tab) && (
                <StyledButton variant="contained" color="secondary" onClick={handleNext('next')} aria-label="Next" disabled={nextDisabled}>
                    Next
                    <MaskedIcon src={RightArrow} color="white" size="26px" />
                </StyledButton>
            )}

            {createAvatarStore.tab === beforeLastTab && (
                <CustomTooltip title="Please set correct username" disableHoverListener={createAvatarStore.usernameIsValid}>
                    <StyledButton
                        variant="contained"
                        color="secondary"
                        onClick={handleNext('submit')}
                        aria-label="Create Avatar"
                        disabled={createDisabled}>
                        Create Avatar
                    </StyledButton>
                </CustomTooltip>
            )}
        </ButtonsContainer>
    );
});
